import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth-lib';
import { Button, Card, Skeleton, ThemeToggle } from '../design-system';
import PlanChooser from '../components/subscription/PlanChooser';
import { useSubscription } from '../components/subscription/useSubscription';
import { STATUS_COPY } from '../lib/plans';
import './SubscriptionOnboarding.css';

// ─── Where a firm without an approved plan lands ────────────────────────────
//
// Deliberately OUTSIDE the cockpit, a sibling of Login and Register rather than
// a Settings screen. A brand-new firm has no clients, no staff and no tasks: the
// org-admin shell would give it a sidebar of twenty sections that all bounce
// straight back, a "Search Clients" box for a firm with no clients, and links to
// other Settings pages it cannot open. That is a poor first impression and a
// confusing one.
//
// It also keeps the gates honest. Reaching this content through a cockpit route
// needed TWO exemptions — skipping the subscription gate AND the nav-permission
// gate — on a route sitting inside the gated tree, where any later change to the
// settings area could silently break the escape hatch. Out here, no exemption is
// needed at all, because it was never in the tree.
//
// ⚠️ It shares components/subscription/PlanChooser with the Settings panel. Two
// screens showing the same state is exactly the drift this codebase keeps
// paying for; two HOSTS of one component is not.

function SubscriptionOnboarding() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { subscription, seats, organization, loading, reload } = useSubscription();

  // ⚠️ Redirect only on the TRANSITION from locked to approved, never because
  // the firm is already active.
  //
  // Somebody sitting on this screen while they wait should be let into the app
  // the moment their request is approved. But an ACTIVE firm arriving here (a
  // stale tab, a bookmark) must not be bounced mid-render — and an active firm
  // that wants a different plan uses the Settings panel, not this screen.
  const wasInactive = useRef(false);
  useEffect(() => {
    if (!subscription) return;
    if (!subscription.isActive) { wasInactive.current = true; return; }
    if (wasInactive.current) navigate('/org-admin', { replace: true });
  }, [subscription, navigate]);

  // While a decision is outstanding, re-check. Somebody watching this screen
  // should not have to work out that they need to refresh.
  useEffect(() => {
    if (subscription?.status !== 'pending') return undefined;
    const id = setInterval(reload, 20_000);
    return () => clearInterval(id);
  }, [subscription, reload]);

  const copy = STATUS_COPY[subscription?.status] || STATUS_COPY.none;

  return (
    <div className="onb-page">
      <header className="onb-header">
        <div>
          <h1>{organization?.name || user?.name || 'Your firm'}</h1>
          <p className="onb-muted">{copy.detail}</p>
        </div>
        <div className="onb-header-actions">
          <ThemeToggle />
          {/* An active firm arriving here can go back to work; a locked-out one
              can only sign out. Offering "Sign out" as the only exit to someone
              whose firm is working is a dead end. */}
          {subscription?.isActive ? (
            <Button variant="secondary" size="sm" onClick={() => navigate('/org-admin')}>
              Back to the app
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => { await logout(); navigate('/login'); }}
            >
              Sign out
            </Button>
          )}
        </div>
      </header>

      {loading ? (
        <Card><Skeleton.Text lines={8} /></Card>
      ) : (
        <>
          <Card className={`onb-status onb-status--${copy.tone}`}>
            <strong>{copy.label}</strong>
            {/* The reason a super admin gave. Without it a refused firm has a
                locked platform and nothing to act on, so they simply ask again. */}
            {subscription?.statusNote && (
              <p className="onb-note">“{subscription.statusNote}”</p>
            )}
          </Card>

          <PlanChooser subscription={subscription} seats={seats} onChanged={reload} />
        </>
      )}
    </div>
  );
}

export default SubscriptionOnboarding;
